import classNames from "classnames";
import React, { Fragment, FunctionComponent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { CaptionBody } from "src/components/Text";
import { localize } from "src/l10n";
import SpintrUser from "src/ui/components/SpintrUser";
import { ContentMetaRowProps } from "./ContentMetaRow.types";
import "./ContentMetaRow.scss";

/** "{n} min lästid", or nothing when there is no reading time. */
export const formatReadTime = (minutes: number | undefined): string =>
    minutes > 0 ?
        minutes + " min " + localize("READ_TIME").toLowerCase() :
        "";

/**
 * The read view's meta row (text pages, news and blog posts):
 * the person's face and name, then the parts and the reading time, joined with " · ".
 */
const ContentMetaRow: FunctionComponent<ContentMetaRowProps> = ({ person, parts, readTimeMinutes, faceSize, className }) => {
    const readTime = formatReadTime(readTimeMinutes);
    const items: ReactNode[] = [
        ...(parts || []),
        ...(readTime ? [readTime] : []),
    ].filter((part) => part !== undefined && part !== null && part !== false && part !== "");

    if (!person && items.length === 0) {
        return null;
    }

    return (
        <div className={classNames("ContentMetaRow", className)}>
            {!!person && (
                <div className="ContentMetaRow-person">
                    <SpintrUser
                        name={person.name}
                        imageUrl={person.imageUrl}
                        size={faceSize || 20}
                        hideText
                        personalName
                    />
                    <CaptionBody as="span" weight="medium" color="contentDark" className="ContentMetaRow-name">
                        {person.id > 0 ?
                            <Link to={"/goto/" + person.id}>{person.name}</Link> :
                            person.name}
                    </CaptionBody>
                </div>
            )}
            {items.length > 0 && (
                <CaptionBody as="p" color="contentNormal" className="ContentMetaRow-parts">
                    {items.map((part, index) => (
                        <Fragment key={index}>
                            {(index > 0 || !!person) && " · "}
                            {part}
                        </Fragment>
                    ))}
                </CaptionBody>
            )}
        </div>
    );
};

export default ContentMetaRow;
